import { useState, useEffect } from 'react';
import { Gift, Plus, Search, Copy, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface GiftCard {
  id: number;
  code: string;
  initial_balance: number;
  current_balance: number;
  recipient_email: string | null;
  recipient_name: string | null;
  sender_name: string | null;
  message: string | null;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

export default function UpsellGiftCards() {
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [filteredCards, setFilteredCards] = useState<GiftCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');

  // Form state for new gift card
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCard, setNewCard] = useState({
    initial_balance: 50,
    recipient_email: '',
    recipient_name: '',
    sender_name: '',
    message: '',
  });

  useEffect(() => {
    fetchGiftCards();
  }, []);

  useEffect(() => {
    filterCards();
  }, [searchTerm, giftCards]);

  async function fetchGiftCards() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ gift_cards: GiftCard[] }>('/admin/gift-cards');
      setGiftCards(response.gift_cards || []);
    } catch (err) {
      setError('Failed to load gift cards. Please try again.');
      console.error('Error fetching gift cards:', err);
    } finally {
      setIsLoading(false);
    }
  }

  function filterCards() {
    if (!searchTerm.trim()) {
      setFilteredCards(giftCards);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = giftCards.filter(
      card =>
        card.code.toLowerCase().includes(term) ||
        card.recipient_email?.toLowerCase().includes(term) ||
        card.recipient_name?.toLowerCase().includes(term)
    );
    setFilteredCards(filtered);
  }

  async function handleCreateGiftCard(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    if (newCard.initial_balance <= 0) {
      setError('Please enter a valid balance amount');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      await api.post('/admin/gift-cards', newCard);
      setSuccessMessage('Gift card created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowCreateForm(false);
      setNewCard({
        initial_balance: 50,
        recipient_email: '',
        recipient_name: '',
        sender_name: '',
        message: '',
      });
      await fetchGiftCards();
    } catch (err) {
      setError('Failed to create gift card. Please try again.');
      console.error('Error creating gift card:', err);
    } finally {
      setIsCreating(false);
    }
  }

  async function handleToggleActive(id: number, currentState: boolean) {
    try {
      setError(null);
      await api.patch(`/admin/gift-cards/${id}`, {
        is_active: !currentState,
      });
      setSuccessMessage(`Gift card ${!currentState ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setGiftCards(prev => prev.map(gc => gc.id === id ? { ...gc, is_active: !currentState } : gc));
    } catch (err) {
      setError('Failed to update gift card. Please try again.');
      console.error('Error updating gift card:', err);
    }
  }

  function handleCopyCode(code: string) {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  }

  function formatCurrency(amount: number) {
    return `$${amount.toFixed(2)}`;
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString();
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading gift cards...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-8 h-8 text-gray-700" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-gray-900">Gift Cards</h1>
            </div>
            <p className="text-gray-600">Manage digital gift cards and check balances</p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            Create Gift Card
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {showCreateForm && (
        <Card title="Create New Gift Card" padding="lg" className="mb-6">
          <form onSubmit={handleCreateGiftCard} className="space-y-4">
            <Input
              label="Initial Balance"
              type="number"
              value={String(newCard.initial_balance)}
              onChange={(e) => setNewCard(prev => ({ ...prev, initial_balance: parseFloat(e.target.value) || 0 }))}
              min="0"
              step="0.01"
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Recipient Email (optional)"
                type="email"
                value={newCard.recipient_email}
                onChange={(e) => setNewCard(prev => ({ ...prev, recipient_email: e.target.value }))}
                placeholder="recipient@example.com"
              />
              <Input
                label="Recipient Name (optional)"
                value={newCard.recipient_name}
                onChange={(e) => setNewCard(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="Jane Doe"
              />
            </div>

            <Input
              label="Sender Name (optional)"
              value={newCard.sender_name}
              onChange={(e) => setNewCard(prev => ({ ...prev, sender_name: e.target.value }))}
              placeholder="John Doe"
            />

            <Input
              label="Gift Message (optional)"
              value={newCard.message}
              onChange={(e) => setNewCard(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Happy Birthday!"
            />

            <div className="flex gap-3">
              <Button type="submit" variant="primary" size="md" isLoading={isCreating}>
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                Create Gift Card
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card padding="lg" className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search by code, email, or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </Card>

      <Card title={`Gift Cards (${filteredCards.length})`} padding="lg">
        {filteredCards.length === 0 ? (
          <p className="text-stone-500 text-center py-8">
            {searchTerm ? 'No gift cards match your search.' : 'No gift cards yet. Click "Create Gift Card" to get started.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Code</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Balance</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Recipient</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Created</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Status</th>
                  <th className="text-right py-3 px-2 font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCards.map(card => (
                  <tr key={card.id} className="border-b border-stone-100">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <code className="text-stone-900 font-mono text-xs bg-stone-100 px-2 py-1 rounded">
                          {card.code}
                        </code>
                        <button
                          type="button"
                          onClick={() => handleCopyCode(card.code)}
                          className="text-stone-400 hover:text-stone-600"
                          title="Copy code"
                        >
                          {copiedCode === card.code ? (
                            <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
                          ) : (
                            <Copy className="w-4 h-4" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-stone-900">
                        {formatCurrency(card.current_balance)}
                        {card.current_balance < card.initial_balance && (
                          <span className="text-stone-500 text-xs ml-1">
                            / {formatCurrency(card.initial_balance)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-stone-600">
                      {card.recipient_name || card.recipient_email || '-'}
                    </td>
                    <td className="py-3 px-2 text-stone-600">{formatDate(card.created_at)}</td>
                    <td className="py-3 px-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          card.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        {card.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(card.id, card.is_active)}
                      >
                        {card.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
